import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import {
  Users, Store, Package, ShoppingBag, TrendingUp, CheckCircle, XCircle,
  Clock, Eye, Star, Loader2, RefreshCw, AlertTriangle, Ban, Mail, UserCheck,
  Shield, ShieldCheck, Settings, Percent, Crown, Lock, FolderTree, Plus, FileText,
  ScanSearch, LayoutTemplate, Upload, ImageIcon, MapPin
} from 'lucide-react';
import { api } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '../components/ui/card';
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '../components/ui/tabs';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { toast } from 'sonner';

const AdminDashboard = () => {
  const [stats, setStats] = useState(null);
  const [permissions, setPermissions] = useState(null);
  const [pendingProducers, setPendingProducers] = useState([]);
  const [allProducers, setAllProducers] = useState([]);
  const [users, setUsers] = useState({ items: [], total: 0 });
  const [admins, setAdmins] = useState([]);
  const [products, setProducts] = useState({ items: [], total: 0 });
  const [reviews, setReviews] = useState({ items: [], total: 0 });
  const [platformSettings, setPlatformSettings] = useState(null);
  const [categories, setCategories] = useState([]);
  const [newCategory, setNewCategory] = useState({ name: '', slug: '', description: '', macro_type: 'FOOD' });
  const [cmsForm, setCmsForm] = useState({
    hero_title: '',
    hero_subtitle: '',
    hero_cta_primary_text: '',
    hero_cta_primary_url: '',
    hero_cta_secondary_text: '',
    hero_cta_secondary_url: '',
  });
  const [cmsHeroPreview, setCmsHeroPreview] = useState(null);
  const [cmsSaving, setCmsSaving] = useState(false);
  const [heroUploading, setHeroUploading] = useState(false);
  const heroInputRef = useRef(null);
  const [identityForm, setIdentityForm] = useState({ platform_name: '', tagline: '' });
  const [logoPreview, setLogoPreview] = useState(null);
  const [faviconPreview, setFaviconPreview] = useState(null);
  const [logoUploading, setLogoUploading] = useState(false);
  const [faviconUploading, setFaviconUploading] = useState(false);
  const [identitySaving, setIdentitySaving] = useState(false);
  const logoInputRef = useRef(null);
  const faviconInputRef = useRef(null);
  const [regions, setRegions] = useState([]);
  const [regionForms, setRegionForms] = useState({});     // { [id]: { name, description } }
  const [regionSaving, setRegionSaving] = useState({});   // { [id]: bool }
  const [regionImgLoading, setRegionImgLoading] = useState({}); // { [id]: bool }
  const [allCategories, setAllCategories] = useState([]);
  const [categoryForms, setCategoryForms] = useState({});
  const [categorySaving, setCategorySaving] = useState({});
  const [categoryImgLoading, setCategoryImgLoading] = useState({});
  const MACRO_LABELS = { FOOD: 'Food', ARTIGIANATO: 'Artigianato', EXPERIENCE: 'Experience' };
  const [macroForms, setMacroForms] = useState({
    FOOD: { title: '', description: '' },
    ARTIGIANATO: { title: '', description: '' },
    EXPERIENCE: { title: '', description: '' },
  });
  const [macroImgPreviews, setMacroImgPreviews] = useState({ FOOD: null, ARTIGIANATO: null, EXPERIENCE: null });
  const [macroSaving, setMacroSaving] = useState({});
  const [macroImgLoading, setMacroImgLoading] = useState({});
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState({});

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      // Fetch permissions first
      const permRes = await api.get('/admin/my-permissions');
      setPermissions(permRes.data);
      
      const [statsRes, pendingRes, productsRes, reviewsRes, usersRes, producersRes, cmsRes, identityRes, regionsRes, categoriesRes, macroRes] = await Promise.all([
        api.get('/admin/stats'),
        api.get('/admin/producers/pending'),
        api.get('/admin/products?page_size=20'),
        api.get('/admin/reviews?page_size=20'),
        api.get('/admin/users?page_size=50'),
        api.get('/admin/producers/all'),
        api.get('/cms/homepage'),
        api.get('/cms/identity'),
        api.get('/catalog/regions'),
        api.get('/catalog/categories'),
        api.get('/cms/macro-types'),
      ]);

      setStats(statsRes.data);
      setPendingProducers(pendingRes.data);
      setProducts(productsRes.data);
      setReviews(reviewsRes.data);
      setUsers(usersRes.data);
      setAllProducers(producersRes.data);
      const cms = cmsRes.data;
      setCmsForm({
        hero_title: cms.hero_title || '',
        hero_subtitle: cms.hero_subtitle || '',
        hero_cta_primary_text: cms.hero_cta_primary_text || '',
        hero_cta_primary_url: cms.hero_cta_primary_url || '',
        hero_cta_secondary_text: cms.hero_cta_secondary_text || '',
        hero_cta_secondary_url: cms.hero_cta_secondary_url || '',
      });
      if (cms.hero_image_url) setCmsHeroPreview(cms.hero_image_url);
      const id = identityRes.data;
      setIdentityForm({ platform_name: id.platform_name || '', tagline: id.tagline || '' });
      if (id.logo_url) setLogoPreview(id.logo_url);
      if (id.favicon_url) setFaviconPreview(id.favicon_url);

      const regs = regionsRes.data;
      setRegions(regs);
      setRegionForms(Object.fromEntries(regs.map(r => [r.id, { name: r.name, description: r.description || '' }])));

      const cats = categoriesRes.data;
      setAllCategories(cats);
      setCategoryForms(Object.fromEntries(cats.map(c => [c.id, { name: c.name, description: c.description || '' }])));

      const macros = macroRes.data;
      setMacroForms(Object.fromEntries(macros.map(m => [m.macro_type, { title: m.title || '', description: m.description || '' }])));
      setMacroImgPreviews(Object.fromEntries(macros.map(m => [m.macro_type, m.image_url || null])));
      
      // Fetch super_admin only data
      if (permRes.data.is_super_admin) {
        const [adminsRes, settingsRes, categoriesRes] = await Promise.all([
          api.get('/admin/admins'),
          api.get('/admin/settings/platform'),
          api.get('/catalog/categories')
        ]);
        setAdmins(adminsRes.data);
        setPlatformSettings(settingsRes.data);
        setCategories(categoriesRes.data);
      }
    } catch (error) {
      console.error('Error fetching admin data:', error);
      toast.error('Errore nel caricamento dei dati');
    } finally {
      setLoading(false);
    }
  };

  const handleProducerAction = async (producerId, action) => {
    setActionLoading(prev => ({ ...prev, [producerId]: true }));
    try {
      await api.post(`/admin/producers/${producerId}/action`, { action });
      const messages = {
        approve: 'Produttore approvato!',
        reject: 'Produttore rifiutato',
        suspend: 'Produttore sospeso',
        feature: 'Produttore messo in evidenza',
        unfeature: 'Produttore rimosso da evidenza'
      };
      toast.success(messages[action] || 'Operazione completata');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore durante l\'operazione');
    } finally {
      setActionLoading(prev => ({ ...prev, [producerId]: false }));
    }
  };

  const handleUserRoleChange = async (userId, newRole) => {
    try {
      await api.post(`/admin/users/${userId}/role`, { role: newRole });
      toast.success(`Ruolo utente aggiornato a ${newRole}`);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore durante l\'aggiornamento del ruolo');
    }
  };

  const handleAdminAction = async (userId, action) => {
    setActionLoading(prev => ({ ...prev, [userId]: true }));
    try {
      const endpoint = action === 'promote' 
        ? `/admin/admins/${userId}/promote`
        : `/admin/admins/${userId}/demote`;
      const res = await api.post(endpoint);
      toast.success(res.data.message);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore durante l\'operazione');
    } finally {
      setActionLoading(prev => ({ ...prev, [userId]: false }));
    }
  };

  const handleUpdateCommission = async (producerId, rate) => {
    try {
      if (producerId) {
        await api.post(`/admin/settings/producer/${producerId}/commission?commission_rate=${rate}`);
        toast.success('Commissione produttore aggiornata');
      } else {
        await api.post(`/admin/settings/commission?commission_rate=${rate}`);
        toast.success('Commissione globale aggiornata');
      }
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore durante l\'aggiornamento');
    }
  };

  const handleProductFeature = async (productId, featured) => {
    try {
      await api.post(`/admin/products/${productId}/feature?featured=${featured}`);
      toast.success(featured ? 'Prodotto messo in evidenza' : 'Prodotto rimosso da evidenza');
      fetchData();
    } catch (error) {
      toast.error('Errore durante l\'operazione');
    }
  };

  const handleReviewAction = async (reviewId, status) => {
    try {
      await api.post(`/admin/reviews/${reviewId}/action`, { status });
      toast.success('Stato recensione aggiornato');
      fetchData();
    } catch (error) {
      toast.error('Errore durante l\'operazione');
    }
  };

  // Funzione per creare nuova categoria (solo SuperAdmin)
  const handleCreateCategory = async (e) => {
    e.preventDefault();
    if (!newCategory.name || !newCategory.slug) {
      toast.error('Nome e Slug sono obbligatori');
      return;
    }
    setActionLoading(prev => ({ ...prev, createCategory: true }));
    try {
      await api.post('/admin/categories', newCategory);
      toast.success(`Categoria "${newCategory.name}" creata con successo`);
      setNewCategory({ name: '', slug: '', description: '', macro_type: 'FOOD' });
      // Refresh categories
      const categoriesRes = await api.get('/catalog/categories');
      setCategories(categoriesRes.data);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore durante la creazione della categoria');
    } finally {
      setActionLoading(prev => ({ ...prev, createCategory: false }));
    }
  };

  // Funzione per generare slug automatico dal nome
  const generateSlug = (name) => {
    return name.toLowerCase()
      .replace(/[àáâãäå]/g, 'a')
      .replace(/[èéêë]/g, 'e')
      .replace(/[ìíîï]/g, 'i')
      .replace(/[òóôõö]/g, 'o')
      .replace(/[ùúûü]/g, 'u')
      .replace(/[^a-z0-9\s-]/g, '')
      .replace(/\s+/g, '-')
      .replace(/-+/g, '-')
      .trim();
  };

  // Badge per macro_type
  const getMacroTypeBadge = (macroType) => {
    const styles = {
      'FOOD': 'bg-green-100 text-green-800',
      'ARTIGIANATO': 'bg-amber-100 text-amber-800',
      'EXPERIENCE': 'bg-blue-100 text-blue-800'
    };
    return styles[macroType] || 'bg-gray-100 text-gray-800';
  };

  const getRoleBadge = (role) => {
    const styles = {
      super_admin: 'bg-purple-100 text-purple-800',
      admin: 'bg-red-100 text-red-800',
      producer: 'bg-blue-100 text-blue-800',
      user: 'bg-gray-100 text-gray-800'
    };
    const labels = {
      super_admin: 'Super Admin',
      admin: 'Admin',
      producer: 'Produttore',
      user: 'Utente'
    };
    const icons = {
      super_admin: <Crown className="w-3 h-3 mr-1" />,
      admin: <ShieldCheck className="w-3 h-3 mr-1" />,
    };
    return (
      <Badge className={`${styles[role] || styles.user} flex items-center`}>
        {icons[role]}
        {labels[role] || role}
      </Badge>
    );
  };

  const getStatusBadge = (status) => {
    const styles = {
      approved: 'bg-green-100 text-green-800',
      pending: 'bg-yellow-100 text-yellow-800',
      rejected: 'bg-red-100 text-red-800',
      suspended: 'bg-orange-100 text-orange-800'
    };
    const labels = {
      approved: 'Approvato',
      pending: 'In attesa',
      rejected: 'Rifiutato',
      suspended: 'Sospeso'
    };
    return (
      <Badge className={styles[status] || 'bg-gray-100'}>
        {labels[status] || status}
      </Badge>
    );
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  const isSuperAdmin = permissions?.is_super_admin;

  const handleMacroImageUpload = async (macroType, file) => {
    if (!file) return;
    setMacroImgLoading(p => ({ ...p, [macroType]: true }));
    try {
      const formData = new FormData();
      formData.append('file', file);
      const res = await api.post(`/upload/cms/macro-type/${macroType}/image`, formData, { headers: { 'Content-Type': 'multipart/form-data' } });
      setMacroImgPreviews(p => ({ ...p, [macroType]: res.data.url }));
      toast.success('Immagine aggiornata');
    } catch { toast.error('Errore caricamento immagine'); }
    finally { setMacroImgLoading(p => ({ ...p, [macroType]: false })); }
  };

  const handleMacroSave = async (macroType) => {
    setMacroSaving(p => ({ ...p, [macroType]: true }));
    try {
      const form = macroForms[macroType];
      const payload = {};
      if (form.title.trim()) payload.title = form.title.trim();
      if (form.description.trim()) payload.description = form.description.trim();
      await api.put(`/cms/macro-types/${macroType}`, payload);
      toast.success(`${MACRO_LABELS[macroType]} salvato`);
    } catch { toast.error('Errore salvataggio'); }
    finally { setMacroSaving(p => ({ ...p, [macroType]: false })); }
  };

  const handleRegionImageUpload = async (regionId, file) => {
    if (!file) return;
    setRegionImgLoading(p => ({ ...p, [regionId]: true }));
    try {
      const formData = new FormData();
      formData.append('file', file);
      const res = await api.post(`/upload/catalog/region/${regionId}/image`, formData, { headers: { 'Content-Type': 'multipart/form-data' } });
      setRegions(prev => prev.map(r => r.id === regionId ? { ...r, image_url: res.data.url } : r));
      toast.success('Immagine regione aggiornata');
    } catch { toast.error('Errore caricamento immagine'); }
    finally { setRegionImgLoading(p => ({ ...p, [regionId]: false })); }
  };

  const handleRegionSave = async (regionId) => {
    setRegionSaving(p => ({ ...p, [regionId]: true }));
    try {
      const form = regionForms[regionId];
      const payload = {};
      if (form.name.trim()) payload.name = form.name.trim();
      if (form.description.trim() !== undefined) payload.description = form.description.trim() || null;
      await api.put(`/catalog/regions/${regionId}`, payload);
      toast.success('Regione salvata');
    } catch { toast.error('Errore salvataggio'); }
    finally { setRegionSaving(p => ({ ...p, [regionId]: false })); }
  };

  const handleCategoryImageUpload = async (categoryId, file) => {
    if (!file) return;
    setCategoryImgLoading(p => ({ ...p, [categoryId]: true }));
    try {
      const formData = new FormData();
      formData.append('file', file);
      const res = await api.post(`/upload/catalog/category/${categoryId}/image`, formData, { headers: { 'Content-Type': 'multipart/form-data' } });
      setAllCategories(prev => prev.map(c => c.id === categoryId ? { ...c, image_url: res.data.url } : c));
      toast.success('Immagine categoria aggiornata');
    } catch { toast.error('Errore caricamento immagine'); }
    finally { setCategoryImgLoading(p => ({ ...p, [categoryId]: false })); }
  };

  const handleCategorySave = async (categoryId) => {
    setCategorySaving(p => ({ ...p, [categoryId]: true }));
    try {
      const form = categoryForms[categoryId];
      const payload = {};
      if (form.name.trim()) payload.name = form.name.trim();
      if (form.description !== undefined) payload.description = form.description.trim() || null;
      await api.put(`/catalog/categories/${categoryId}`, payload);
      toast.success('Categoria salvata');
    } catch { toast.error('Errore salvataggio'); }
    finally { setCategorySaving(p => ({ ...p, [categoryId]: false })); }
  };

  const handleLogoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setLogoUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const res = await api.post('/upload/cms/logo', formData, { headers: { 'Content-Type': 'multipart/form-data' } });
      setLogoPreview(res.data.url);
      toast.success('Logo aggiornato');
    } catch { toast.error('Errore caricamento logo'); }
    finally { setLogoUploading(false); }
  };

  const handleFaviconUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setFaviconUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const res = await api.post('/upload/cms/favicon', formData, { headers: { 'Content-Type': 'multipart/form-data' } });
      setFaviconPreview(res.data.url);
      toast.success('Favicon aggiornata');
    } catch { toast.error('Errore caricamento favicon'); }
    finally { setFaviconUploading(false); }
  };

  const handleIdentitySave = async () => {
    setIdentitySaving(true);
    try {
      const payload = {};
      if (identityForm.platform_name.trim()) payload.platform_name = identityForm.platform_name.trim();
      if (identityForm.tagline.trim()) payload.tagline = identityForm.tagline.trim();
      await api.put('/cms/identity', payload);
      toast.success('Identità sito aggiornata — ricarica la pagina per vedere il logo');
    } catch { toast.error('Errore salvataggio identità'); }
    finally { setIdentitySaving(false); }
  };

  const handleHeroImageUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setHeroUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const res = await api.post('/upload/cms/hero-image', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setCmsHeroPreview(res.data.url);
      toast.success('Immagine hero aggiornata');
    } catch {
      toast.error('Errore caricamento immagine');
    } finally {
      setHeroUploading(false);
    }
  };

  const handleCmsSave = async () => {
    setCmsSaving(true);
    try {
      const payload = {};
      for (const [k, v] of Object.entries(cmsForm)) {
        if (v.trim() !== '') payload[k] = v.trim();
      }
      await api.put('/cms/homepage', payload);
      toast.success('Homepage aggiornata');
    } catch {
      toast.error('Errore salvataggio CMS');
    } finally {
      setCmsSaving(false);
    }
  };

  return (
    <div className="min-h-screen bg-background" data-testid="admin-dashboard">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="flex justify-between items-center mb-8">
          <div>
            <div className="flex items-center gap-3 mb-1">
              <h1 className="font-heading text-3xl font-bold text-text-primary">
                Dashboard Amministrazione
              </h1>
              {isSuperAdmin ? (
                <Badge className="bg-purple-600 text-white flex items-center gap-1">
                  <Crown className="w-3 h-3" />
                  Super Admin
                </Badge>
              ) : (
                <Badge className="bg-red-600 text-white flex items-center gap-1">
                  <Shield className="w-3 h-3" />
                  Admin
                </Badge>
              )}
            </div>
            <p className="text-text-secondary mt-1">
              {isSuperAdmin 
                ? 'Accesso completo - Gestisci admin, utenti, produttori e impostazioni'
                : 'Gestisci produttori, prodotti e recensioni'}
            </p>
          </div>
          <Button onClick={fetchData} variant="outline" className="gap-2">
            <RefreshCw className="w-4 h-4" />
            Aggiorna
          </Button>
        </div>

        {/* Quick Links to Payment Dashboards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
          <Link to="/admin/pagamenti">
            <Card className="hover:shadow-lg transition-shadow cursor-pointer border-l-4 border-l-green-500">
              <CardHeader className="pb-2">
                <CardTitle className="text-lg flex items-center gap-2">
                  <TrendingUp className="w-5 h-5 text-green-600" />
                  Gestione Payout Venditori
                </CardTitle>
                <CardDescription>
                  Dashboard operativa per elaborare i pagamenti quindicinali ai venditori
                </CardDescription>
              </CardHeader>
            </Card>
          </Link>
          
          <Link to="/admin/legal">
            <Card className="hover:shadow-lg transition-shadow cursor-pointer border-l-4 border-l-blue-500">
              <CardHeader className="pb-2">
                <CardTitle className="text-lg flex items-center gap-2">
                  <FileText className="w-5 h-5 text-blue-600" />
                  Documenti Legali
                </CardTitle>
                <CardDescription>
                  Gestisci termini, privacy policy e configurazione legale della piattaforma
                </CardDescription>
              </CardHeader>
              <CardContent className="pt-0">
                <span
                  className="inline-flex items-center gap-1 text-xs font-medium text-blue-600 hover:text-blue-900 hover:underline transition-colors"
                  onClick={e => { e.preventDefault(); e.stopPropagation(); window.location.href = '/admin/legal/audit'; }}
                >
                  <ScanSearch className="w-3.5 h-3.5" />
                  Audit accettazioni
                </span>
              </CardContent>
            </Card>
          </Link>

          {isSuperAdmin && (
            <Link to="/admin/analytics">
              <Card className="hover:shadow-lg transition-shadow cursor-pointer border-l-4 border-l-purple-500">
                <CardHeader className="pb-2">
                  <CardTitle className="text-lg flex items-center gap-2">
                    <Crown className="w-5 h-5 text-purple-600" />
                    Analytics & Audit
                  </CardTitle>
                  <CardDescription>
                    KPI globali, breakdown per venditore, alert e export dati
                  </CardDescription>
                </CardHeader>
              </Card>
            </Link>
          )}
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          <Card>
            <CardHeader className="pb-2">
              <CardDescription className="flex items-center gap-2">
                <Users className="w-4 h-4" />
                Utenti Totali
              </CardDescription>
            </CardHeader>
            <CardContent>
              <p className="text-3xl font-bold">{stats?.users || 0}</p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="pb-2">
              <CardDescription className="flex items-center gap-2">
                <Store className="w-4 h-4" />
                Produttori
              </CardDescription>
            </CardHeader>
            <CardContent>
              <p className="text-3xl font-bold">{stats?.producers_approved || 0}</p>
              {stats?.producers_pending > 0 && (
                <p className="text-sm text-orange-500 font-medium">
                  +{stats.producers_pending} in attesa
                </p>
              )}
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="pb-2">
              <CardDescription className="flex items-center gap-2">
                <Package className="w-4 h-4" />
                Prodotti Attivi
              </CardDescription>
            </CardHeader>
            <CardContent>
              <p className="text-3xl font-bold">{stats?.products_active || 0}</p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="pb-2">
              <CardDescription className="flex items-center gap-2">
                <TrendingUp className="w-4 h-4" />
                Commissioni
              </CardDescription>
            </CardHeader>
            <CardContent>
              <p className="text-3xl font-bold">€{(stats?.commission_total || 0).toFixed(2)}</p>
              <p className="text-sm text-text-muted">
                su €{(stats?.revenue_total || 0).toFixed(2)} vendite
              </p>
            </CardContent>
          </Card>
        </div>

        {/* Main Content Tabs */}
        <Tabs defaultValue="pending" className="space-y-4">
          <TabsList className="flex-wrap">
            <TabsTrigger value="pending" className="gap-2">
              <Clock className="w-4 h-4" />
              In Attesa
              {pendingProducers.length > 0 && (
                <Badge variant="destructive" className="ml-1">
                  {pendingProducers.length}
                </Badge>
              )}
            </TabsTrigger>
            <TabsTrigger value="producers" className="gap-2">
              <Store className="w-4 h-4" />
              Produttori
            </TabsTrigger>
            <TabsTrigger value="users" className="gap-2">
              <Users className="w-4 h-4" />
              Utenti
            </TabsTrigger>
            <TabsTrigger value="products" className="gap-2">
              <Package className="w-4 h-4" />
              Prodotti
            </TabsTrigger>
            <TabsTrigger value="reviews" className="gap-2">
              <Star className="w-4 h-4" />
              Recensioni
            </TabsTrigger>
            <TabsTrigger value="cms" className="gap-2">
              <LayoutTemplate className="w-4 h-4" />
              CMS Homepage
            </TabsTrigger>

            {/* SUPER_ADMIN ONLY TABS */}
            {isSuperAdmin && (
              <>
                <TabsTrigger value="admins" className="gap-2">
                  <ShieldCheck className="w-4 h-4" />
                  Amministratori
                </TabsTrigger>
                <TabsTrigger value="settings" className="gap-2">
                  <Settings className="w-4 h-4" />
                  Impostazioni
                </TabsTrigger>
                <TabsTrigger value="categories" className="gap-2">
                  <FolderTree className="w-4 h-4" />
                  Categorie
                </TabsTrigger>
              </>
            )}
          </TabsList>

          {/* Pending Producers Tab */}
          <TabsContent value="pending">
            <Card>
              <CardHeader>
                <CardTitle>Produttori in Attesa di Approvazione</CardTitle>
                <CardDescription>
                  Approva o rifiuta le richieste dei nuovi produttori
                </CardDescription>
              </CardHeader>
              <CardContent>
                {pendingProducers.length === 0 ? (
                  <div className="text-center py-12 text-text-muted">
                    <CheckCircle className="w-12 h-12 mx-auto mb-4 text-secondary" />
                    <p className="font-medium">Nessuna richiesta in attesa</p>
                    <p className="text-sm">Tutte le richieste sono state gestite</p>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {pendingProducers.map((producer) => (
                      <div 
                        key={producer.id} 
                        className="flex items-start justify-between p-4 bg-background rounded-lg border border-border"
                      >
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-1">
                            <h3 className="font-semibold">{producer.name}</h3>
                            {getStatusBadge(producer.status)}
                          </div>
                          <p className="text-sm text-text-secondary line-clamp-2 mb-2">
                            {producer.description || 'Nessuna descrizione'}
                          </p>
                          <p className="text-xs text-text-muted">
                            Richiesta: {new Date(producer.created_at).toLocaleDateString('it-IT')}
                          </p>
                        </div>
                        <div className="flex gap-2 ml-4">
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => handleProducerAction(producer.id, 'reject')}
                            disabled={actionLoading[producer.id]}
                            className="text-red-600 border-red-200 hover:bg-red-50"
                          >
                            <XCircle className="w-4 h-4 mr-1" />
                            Rifiuta
                          </Button>
                          <Button
                            size="sm"
                            onClick={() => handleProducerAction(producer.id, 'approve')}
                            disabled={actionLoading[producer.id]}
                            className="bg-secondary hover:bg-secondary/90"
                          >
                            {actionLoading[producer.id] ? (
                              <Loader2 className="w-4 h-4 animate-spin" />
                            ) : (
                              <>
                                <CheckCircle className="w-4 h-4 mr-1" />
                                Approva
                              </>
                            )}
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* All Producers Tab */}
          <TabsContent value="producers">
            <Card>
              <CardHeader>
                <CardTitle>Tutti i Produttori</CardTitle>
                <CardDescription>
                  Gestisci tutti i produttori approvati e sospesi
                </CardDescription>
              </CardHeader>
              <CardContent>
                {allProducers.length === 0 ? (
                  <div className="text-center py-12 text-text-muted">
                    <Store className="w-12 h-12 mx-auto mb-4" />
                    <p className="font-medium">Nessun produttore</p>
                  </div>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead>
                        <tr className="border-b border-border">
                          <th className="text-left py-3 px-2 text-sm font-medium text-text-muted">Nome</th>
                          <th className="text-left py-3 px-2 text-sm font-medium text-text-muted">Email</th>
                          <th className="text-left py-3 px-2 text-sm font-medium text-text-muted">Stato</th>
                          <th className="text-left py-3 px-2 text-sm font-medium text-text-muted">Piano</th>
                          <th className="text-left py-3 px-2 text-sm font-medium text-text-muted">Commissione</th>
                          <th className="text-left py-3 px-2 text-sm font-medium text-text-muted">Prodotti</th>
                          <th className="text-right py-3 px-2 text-sm font-medium text-text-muted">Azioni</th>
                        </tr>
                      </thead>
                      <tbody>
                        {allProducers.map(producer => (
                          <tr key={producer.id} className="border-b border-border last:border-0 hover:bg-muted/50">
                            <td className="py-3 px-2">
                              <div className="flex items-center gap-2">
                                <span className="font-medium">{producer.name}</span>
                                {producer.is_featured && (
                                  <Star className="w-4 h-4 fill-yellow-400 text-yellow-400" />
                                )}
                              </div>
                            </td>
                            <td className="py-3 px-2 text-sm text-text-muted">
                              {producer.user_email || '-'}
                            </td>
                            <td className="py-3 px-2">{getStatusBadge(producer.status)}</td>
                            <td className="py-3 px-2">
                              <Badge variant={producer.plan_type === 'pro' ? 'default' : 'outline'}>
                                {producer.plan_type === 'pro' ? 'Pro' : 'Free'}
                              </Badge>
                            </td>
                            <td className="py-3 px-2 text-sm">
                              {isSuperAdmin ? (
                                <div className="flex items-center gap-1">
                                  <Input 
                                    type="number" 
                                    defaultValue={(producer.commission_rate * 100).toFixed(0)}
                                    className="w-16 h-7 text-xs"
                                    min="0"
                                    max="100"
                                    onBlur={(e) => {
                                      const newRate = parseFloat(e.target.value) / 100;
                                      if (newRate !== producer.commission_rate) {
                                        handleUpdateCommission(producer.id, newRate);
                                      }
                                    }}
                                  />
                                  <span className="text-xs">%</span>
                                </div>
                              ) : (
                                <span>{(producer.commission_rate * 100).toFixed(0)}%</span>
                              )}
                            </td>
                            <td className="py-3 px-2 text-sm">
                              {producer.product_count || 0}
                            </td>
                            <td className="py-3 px-2 text-right">
                              <div className="flex gap-1 justify-end">
                                {producer.status === 'approved' && (
                                  <Button
                                    size="sm"
                                    variant="outline"
                                    onClick={() => handleProducerAction(producer.id, 'suspend')}
                                    disabled={actionLoading[producer.id]}
                                    className="text-orange-600 hover:bg-orange-50"
                                    title="Sospendi"
                                  >
                                    <Ban className="w-4 h-4" />
                                  </Button>
                                )}
                                {producer.status === 'suspended' && (
                                  <Button
                                    size="sm"
                                    variant="outline"
                                    onClick={() => handleProducerAction(producer.id, 'approve')}
                                    disabled={actionLoading[producer.id]}
                                    className="text-green-600 hover:bg-green-50"
                                    title="Riattiva"
                                  >
                                    <UserCheck className="w-4 h-4" />
                                  </Button>
                                )}
                                <Button
                                  size="sm"
                                  variant="outline"
                                  onClick={() => handleProducerAction(producer.id, producer.is_featured ? 'unfeature' : 'feature')}
                                  disabled={actionLoading[producer.id]}
                                  title={producer.is_featured ? 'Rimuovi evidenza' : 'Metti in evidenza'}
                                >
                                  <Star className={`w-4 h-4 ${producer.is_featured ? 'fill-yellow-400 text-yellow-400' : ''}`} />
                                </Button>
                                <Link to={`/produttori/${producer.slug}`}>
                                  <Button size="sm" variant="outline" title="Visualizza">
                                    <Eye className="w-4 h-4" />
                                  </Button>
                                </Link>
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Users Tab */}
          <TabsContent value="users">
            <Card>
              <CardHeader>
                <CardTitle>Gestione Utenti</CardTitle>
                <CardDescription>
                  {isSuperAdmin 
                    ? 'Visualizza e gestisci tutti gli utenti registrati'
                    : 'Visualizza utenti e produttori (non puoi modificare admin)'}
                </CardDescription>
              </CardHeader>
              <CardContent>
                {users.items.length === 0 ? (
                  <div className="text-center py-12 text-text-muted">
                    <Users className="w-12 h-12 mx-auto mb-4" />
                    <p className="font-medium">Nessun utente</p>
                  </div>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead>
                        <tr className="border-b border-border">
                          <th className="text-left py-3 px-2 text-sm font-medium text-text-muted">Utente</th>
                          <th className="text-left py-3 px-2 text-sm font-medium text-text-muted">Email</th>
                          <th className="text-left py-3 px-2 text-sm font-medium text-text-muted">Ruolo</th>
                          <th className="text-left py-3 px-2 text-sm font-medium text-text-muted">Stato</th>
                          <th className="text-left py-3 px-2 text-sm font-medium text-text-muted">Registrato</th>
                          <th className="text-right py-3 px-2 text-sm font-medium text-text-muted">Azioni</th>
                        </tr>
                      </thead>
                      <tbody>
                        {users.items.map(user => (
                          <tr key={user.id} className="border-b border-border last:border-0 hover:bg-muted/50">
                            <td className="py-3 px-2">
                              <div className="flex items-center gap-2">
                                <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center">
                                  <span className="text-sm font-medium text-primary">
                                    {(user.first_name?.[0] || user.email[0]).toUpperCase()}
                                  </span>
                                </div>
                                <span className="font-medium">
                                  {user.first_name && user.last_name 
                                    ? `${user.first_name} ${user.last_name}`
                                    : user.email.split('@')[0]}
                                </span>
                              </div>
                            </td>
                            <td className="py-3 px-2">
                              <div className="flex items-center gap-1 text-sm">
                                <Mail className="w-3 h-3 text-text-muted" />
                                {user.email}
                              </div>
                            </td>
                            <td className="py-3 px-2">{getRoleBadge(user.role)}</td>
                            <td className="py-3 px-2">
                              <Badge variant={user.is_active ? 'secondary' : 'destructive'}>
                                {user.is_active ? 'Attivo' : 'Disattivo'}
                              </Badge>
                            </td>
                            <td className="py-3 px-2 text-sm text-text-muted">
                              {user.created_at ? new Date(user.created_at).toLocaleDateString('it-IT') : '-'}
                            </td>
                            <td className="py-3 px-2 text-right">
                              {user.can_modify ? (
                                <select
                                  value={user.role}
                                  onChange={(e) => handleUserRoleChange(user.id, e.target.value)}
                                  className="text-xs border border-border rounded px-2 py-1"
                                >
                                  <option value="user">Utente</option>
                                  <option value="producer">Produttore</option>
                                  {isSuperAdmin && <option value="admin">Admin</option>}
                                </select>
                              ) : (
                                <span className="text-xs text-text-muted flex items-center gap-1 justify-end">
                                  <Lock className="w-3 h-3" />
                                  Protetto
                                </span>
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
                {users.total > users.items.length && (
                  <p className="text-center text-sm text-text-muted mt-4">
                    Mostrando {users.items.length} di {users.total} utenti
                  </p>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Products Tab */}
          <TabsContent value="products">
            <Card>
              <CardHeader>
                <CardTitle>Gestione Prodotti</CardTitle>
                <CardDescription>
                  Visualizza e gestisci tutti i prodotti nel marketplace
                </CardDescription>
              </CardHeader>
              <CardContent>
                {products.items.length === 0 ? (
                  <div className="text-center py-12 text-text-muted">
                    <Package className="w-12 h-12 mx-auto mb-4" />
                    <p className="font-medium">Nessun prodotto</p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {products.items.map((product) => (
                      <div 
                        key={product.id}
                        className="flex items-center justify-between p-3 bg-background rounded-lg border border-border"
                      >
                        <div className="flex-1">
                          <div className="flex items-center gap-2">
                            <h4 className="font-medium">{product.name}</h4>
                            <Badge variant={product.status === 'active' ? 'secondary' : 'outline'}>
                              {product.status}
                            </Badge>
                            {product.is_featured && (
                              <Badge className="bg-primary">In evidenza</Badge>
                            )}
                          </div>
                          <p className="text-sm text-text-muted">€{product.price?.toFixed(2)}</p>
                        </div>
                        <div className="flex gap-2">
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => handleProductFeature(product.id, !product.is_featured)}
                          >
                            <Star className={`w-4 h-4 ${product.is_featured ? 'fill-yellow-400 text-yellow-400' : ''}`} />
                          </Button>
                          <Link to={`/prodotti/${product.slug}`}>
                            <Button size="sm" variant="outline">
                              <Eye className="w-4 h-4" />
                            </Button>
                          </Link>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Reviews Tab */}
          <TabsContent value="reviews">
            <Card>
              <CardHeader>
                <CardTitle>Moderazione Recensioni</CardTitle>
                <CardDescription>
                  Gestisci le recensioni dei clienti
                </CardDescription>
              </CardHeader>
              <CardContent>
                {reviews.items.length === 0 ? (
                  <div className="text-center py-12 text-text-muted">
                    <Star className="w-12 h-12 mx-auto mb-4" />
                    <p className="font-medium">Nessuna recensione</p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {reviews.items.map((review) => (
                      <div 
                        key={review.id}
                        className="p-3 bg-background rounded-lg border border-border"
                      >
                        <div className="flex items-start justify-between">
                          <div className="flex-1">
                            <div className="flex items-center gap-2 mb-1">
                              <div className="flex items-center">
                                {[...Array(5)].map((_, i) => (
                                  <Star 
                                    key={i} 
                                    className={`w-4 h-4 ${i < review.rating ? 'fill-yellow-400 text-yellow-400' : 'text-gray-300'}`} 
                                  />
                                ))}
                              </div>
                              <Badge variant={review.status === 'published' ? 'secondary' : 'outline'}>
                                {review.status}
                              </Badge>
                            </div>
                            <p className="text-sm text-text-secondary">
                              {review.comment || 'Nessun commento'}
                            </p>
                          </div>
                          <div className="flex gap-1 ml-4">
                            {review.status !== 'published' && (
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => handleReviewAction(review.id, 'published')}
                                className="text-xs"
                              >
                                Pubblica
                              </Button>
                            )}
                            {review.status !== 'hidden' && (
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => handleReviewAction(review.id, 'hidden')}
                                className="text-xs text-red-600"
                              >
                                Nascondi
                              </Button>
                            )}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* SUPER_ADMIN ONLY: Admins Management Tab */}
          {isSuperAdmin && (
            <TabsContent value="admins">
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <ShieldCheck className="w-5 h-5 text-purple-600" />
                    Gestione Amministratori
                  </CardTitle>
                  <CardDescription>
                    Promuovi o rimuovi privilegi di amministratore (solo Super Admin)
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    {admins.map(admin => (
                      <div 
                        key={admin.id}
                        className={`flex items-center justify-between p-4 rounded-lg border ${
                          admin.role === 'super_admin' 
                            ? 'border-purple-200 bg-purple-50' 
                            : 'border-border bg-background'
                        }`}
                      >
                        <div className="flex items-center gap-3">
                          <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                            admin.role === 'super_admin' ? 'bg-purple-100' : 'bg-red-100'
                          }`}>
                            {admin.role === 'super_admin' ? (
                              <Crown className="w-5 h-5 text-purple-600" />
                            ) : (
                              <ShieldCheck className="w-5 h-5 text-red-600" />
                            )}
                          </div>
                          <div>
                            <div className="flex items-center gap-2">
                              <p className="font-medium">
                                {admin.first_name && admin.last_name 
                                  ? `${admin.first_name} ${admin.last_name}`
                                  : admin.email.split('@')[0]}
                              </p>
                              {admin.is_self && (
                                <Badge variant="outline" className="text-xs">Tu</Badge>
                              )}
                            </div>
                            <p className="text-sm text-text-muted">{admin.email}</p>
                          </div>
                        </div>
                        <div className="flex items-center gap-3">
                          {getRoleBadge(admin.role)}
                          {admin.role === 'admin' && !admin.is_self && (
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => handleAdminAction(admin.id, 'demote')}
                              disabled={actionLoading[admin.id]}
                              className="text-red-600 border-red-200 hover:bg-red-50"
                            >
                              {actionLoading[admin.id] ? (
                                <Loader2 className="w-4 h-4 animate-spin" />
                              ) : (
                                'Rimuovi Admin'
                              )}
                            </Button>
                          )}
                          {admin.role === 'super_admin' && (
                            <span className="text-xs text-purple-600 flex items-center gap-1">
                              <Lock className="w-3 h-3" />
                              Protetto
                            </span>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>

                  {/* Promote new admin section */}
                  <div className="mt-6 pt-6 border-t border-border">
                    <h4 className="font-medium mb-3">Promuovi nuovo Admin</h4>
                    <p className="text-sm text-text-muted mb-4">
                      Seleziona un utente dalla lista utenti e cambia il suo ruolo ad "Admin"
                    </p>
                  </div>
                </CardContent>
              </Card>
            </TabsContent>
          )}

          {/* SUPER_ADMIN ONLY: Platform Settings Tab */}
          {isSuperAdmin && (
            <TabsContent value="settings">
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Settings className="w-5 h-5" />
                    Impostazioni Piattaforma
                  </CardTitle>
                  <CardDescription>
                    Configura le impostazioni globali del marketplace (solo Super Admin)
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="space-y-6">
                    {/* Commission Settings */}
                    <div className="p-4 border border-border rounded-lg">
                      <h4 className="font-medium flex items-center gap-2 mb-3">
                        <Percent className="w-4 h-4" />
                        Commissioni
                      </h4>
                      <div className="grid gap-4">
                        <div className="flex items-center justify-between">
                          <div>
                            <p className="font-medium">Commissione Default</p>
                            <p className="text-sm text-text-muted">
                              Applicata a tutti i nuovi produttori
                            </p>
                          </div>
                          <div className="flex items-center gap-2">
                            <Input 
                              type="number" 
                              defaultValue={platformSettings?.default_commission_rate * 100 || 20}
                              className="w-20"
                              min="0"
                              max="100"
                              id="default-commission"
                            />
                            <span>%</span>
                            <Button 
                              size="sm"
                              onClick={() => {
                                const input = document.getElementById('default-commission');
                                handleUpdateCommission(null, parseFloat(input.value) / 100);
                              }}
                            >
                              Applica a Tutti
                            </Button>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Plan Limits */}
                    <div className="p-4 border border-border rounded-lg">
                      <h4 className="font-medium flex items-center gap-2 mb-3">
                        <Package className="w-4 h-4" />
                        Limiti Piani
                      </h4>
                      <div className="grid gap-4">
                        <div className="flex items-center justify-between">
                          <div>
                            <p className="font-medium">Piano Free</p>
                            <p className="text-sm text-text-muted">Limite prodotti</p>
                          </div>
                          <span className="text-lg font-bold">
                            {platformSettings?.free_plan_product_limit || 10} prodotti
                          </span>
                        </div>
                        <div className="flex items-center justify-between">
                          <div>
                            <p className="font-medium">Piano Pro</p>
                            <p className="text-sm text-text-muted">
                              €{platformSettings?.pro_plan_price || 29.99}/mese
                            </p>
                          </div>
                          <span className="text-lg font-bold">
                            {platformSettings?.pro_plan_product_limit || 100} prodotti
                          </span>
                        </div>
                      </div>
                    </div>

                    {/* Security Notice */}
                    <div className="p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
                      <h4 className="font-medium flex items-center gap-2 text-yellow-800 mb-2">
                        <AlertTriangle className="w-4 h-4" />
                        Nota sulla Sicurezza
                      </h4>
                      <p className="text-sm text-yellow-700">
                        Le modifiche alle impostazioni della piattaforma sono irreversibili e influenzano 
                        tutti gli utenti. Procedi con cautela. Solo i Super Admin possono accedere a questa sezione.
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </TabsContent>
          )}

          {/* CMS Homepage Tab */}
          <TabsContent value="cms">
            <div className="space-y-6">

              {/* Identità Sito */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <ImageIcon className="w-5 h-5 text-blue-600" />
                    Identità Sito
                  </CardTitle>
                  <CardDescription>
                    Logo, favicon, nome del sito e tagline — applicati globalmente in header e tab browser
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-6">
                  <div className="grid md:grid-cols-2 gap-6">
                    {/* Logo */}
                    <div className="space-y-3">
                      <label className="text-sm font-medium">Logo</label>
                      <div
                        className="relative h-24 rounded-lg border-2 border-dashed border-border bg-muted/30 flex items-center justify-center cursor-pointer hover:border-primary transition-colors overflow-hidden"
                        onClick={() => logoInputRef.current?.click()}
                      >
                        {logoPreview ? (
                          <img src={logoPreview} alt="Logo preview" className="h-full w-full object-contain p-2" />
                        ) : (
                          <div className="flex flex-col items-center gap-1 text-text-muted">
                            <ImageIcon className="w-8 h-8" />
                            <span className="text-xs font-medium text-primary">800 × 200 px (orizzontale)</span>
                            <span className="text-xs text-text-muted">o quadrato 400×400</span>
                          </div>
                        )}
                        {logoUploading && (
                          <div className="absolute inset-0 bg-black/50 flex items-center justify-center">
                            <Loader2 className="w-6 h-6 text-white animate-spin" />
                          </div>
                        )}
                      </div>
                      <input ref={logoInputRef} type="file" accept="image/*" className="hidden" onChange={handleLogoUpload} />
                      <Button variant="outline" size="sm" className="gap-2 w-full" onClick={() => logoInputRef.current?.click()} disabled={logoUploading}>
                        {logoUploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
                        {logoUploading ? 'Caricamento...' : 'Carica Logo'}
                      </Button>
                    </div>

                    {/* Favicon */}
                    <div className="space-y-3">
                      <label className="text-sm font-medium">Favicon</label>
                      <div
                        className="relative h-24 rounded-lg border-2 border-dashed border-border bg-muted/30 flex items-center justify-center cursor-pointer hover:border-primary transition-colors overflow-hidden"
                        onClick={() => faviconInputRef.current?.click()}
                      >
                        {faviconPreview ? (
                          <img src={faviconPreview} alt="Favicon preview" className="w-12 h-12 object-contain" />
                        ) : (
                          <div className="flex flex-col items-center gap-1 text-text-muted">
                            <ImageIcon className="w-8 h-8" />
                            <span className="text-xs font-medium text-primary">64 × 64 px (1:1)</span>
                            <span className="text-xs text-text-muted">PNG o ICO consigliato</span>
                          </div>
                        )}
                        {faviconUploading && (
                          <div className="absolute inset-0 bg-black/50 flex items-center justify-center">
                            <Loader2 className="w-6 h-6 text-white animate-spin" />
                          </div>
                        )}
                      </div>
                      <input ref={faviconInputRef} type="file" accept="image/*" className="hidden" onChange={handleFaviconUpload} />
                      <Button variant="outline" size="sm" className="gap-2 w-full" onClick={() => faviconInputRef.current?.click()} disabled={faviconUploading}>
                        {faviconUploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
                        {faviconUploading ? 'Caricamento...' : 'Carica Favicon'}
                      </Button>
                    </div>
                  </div>

                  <div className="grid md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <label className="text-sm font-medium">Nome sito</label>
                      <Input
                        placeholder="es. Dal Sud"
                        value={identityForm.platform_name}
                        onChange={(e) => setIdentityForm(p => ({ ...p, platform_name: e.target.value }))}
                      />
                      <p className="text-xs text-text-muted">Usato quando il logo non è impostato</p>
                    </div>
                    <div className="space-y-2">
                      <label className="text-sm font-medium">Tagline</label>
                      <Input
                        placeholder="es. Sapori autentici del Sud Italia"
                        value={identityForm.tagline}
                        onChange={(e) => setIdentityForm(p => ({ ...p, tagline: e.target.value }))}
                      />
                    </div>
                  </div>

                  <div className="flex justify-end pt-2 border-t border-border">
                    <Button onClick={handleIdentitySave} disabled={identitySaving} className="gap-2">
                      {identitySaving ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle className="w-4 h-4" />}
                      {identitySaving ? 'Salvataggio...' : 'Salva Identità'}
                    </Button>
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <LayoutTemplate className="w-5 h-5 text-green-600" />
                    Hero Homepage
                  </CardTitle>
                  <CardDescription>
                    Modifica immagine, titolo, sottotitolo e bottoni call-to-action della homepage
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-6">
                  {/* Hero Image Upload */}
                  <div className="space-y-3">
                    <label className="text-sm font-medium">Immagine Hero</label>
                    <div
                      className="relative w-full h-48 rounded-lg overflow-hidden border-2 border-dashed border-border bg-muted/30 cursor-pointer hover:border-primary transition-colors"
                      onClick={() => heroInputRef.current?.click()}
                    >
                      {cmsHeroPreview ? (
                        <img src={cmsHeroPreview} alt="Hero preview" className="w-full h-full object-cover" />
                      ) : (
                        <div className="flex flex-col items-center justify-center h-full gap-2 text-text-muted">
                          <ImageIcon className="w-10 h-10" />
                          <span className="text-sm">Clicca per caricare l'immagine hero</span>
                          <span className="text-xs font-medium text-primary">Consigliato: 1920 × 800 px (ratio 21:9)</span>
                          <span className="text-xs text-text-muted">JPG/PNG/WebP — ottimizzato automaticamente</span>
                        </div>
                      )}
                      {heroUploading && (
                        <div className="absolute inset-0 bg-black/50 flex items-center justify-center">
                          <Loader2 className="w-8 h-8 text-white animate-spin" />
                        </div>
                      )}
                    </div>
                    <input
                      ref={heroInputRef}
                      type="file"
                      accept="image/*"
                      className="hidden"
                      onChange={handleHeroImageUpload}
                    />
                    <div className="flex items-center justify-between">
                      <Button
                        variant="outline"
                        size="sm"
                        className="gap-2"
                        onClick={() => heroInputRef.current?.click()}
                        disabled={heroUploading}
                      >
                        {heroUploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
                        {heroUploading ? 'Caricamento...' : 'Cambia immagine'}
                      </Button>
                      <span className="text-xs text-text-muted">Consigliato: <strong>1920 × 800 px</strong> · ratio 21:9 · JPG/PNG/WebP</span>
                    </div>
                  </div>

                  {/* Text fields */}
                  <div className="grid gap-4">
                    <div className="space-y-2">
                      <label className="text-sm font-medium">Titolo Hero</label>
                      <Input
                        placeholder="es. I Sapori Autentici del Sud Italia"
                        value={cmsForm.hero_title}
                        onChange={(e) => setCmsForm(p => ({ ...p, hero_title: e.target.value }))}
                      />
                    </div>
                    <div className="space-y-2">
                      <label className="text-sm font-medium">Sottotitolo</label>
                      <Input
                        placeholder="es. Scopri prodotti selezionati direttamente dai produttori..."
                        value={cmsForm.hero_subtitle}
                        onChange={(e) => setCmsForm(p => ({ ...p, hero_subtitle: e.target.value }))}
                      />
                    </div>
                  </div>

                  {/* CTA buttons */}
                  <div className="grid md:grid-cols-2 gap-4">
                    <div className="space-y-3 p-4 border border-border rounded-lg">
                      <p className="text-sm font-medium">Bottone Primario (verde)</p>
                      <div className="space-y-2">
                        <label className="text-xs text-text-muted">Testo</label>
                        <Input
                          placeholder="es. Esplora i Prodotti"
                          value={cmsForm.hero_cta_primary_text}
                          onChange={(e) => setCmsForm(p => ({ ...p, hero_cta_primary_text: e.target.value }))}
                        />
                      </div>
                      <div className="space-y-2">
                        <label className="text-xs text-text-muted">URL (path relativo)</label>
                        <Input
                          placeholder="es. /prodotti"
                          value={cmsForm.hero_cta_primary_url}
                          onChange={(e) => setCmsForm(p => ({ ...p, hero_cta_primary_url: e.target.value }))}
                        />
                      </div>
                    </div>
                    <div className="space-y-3 p-4 border border-border rounded-lg">
                      <p className="text-sm font-medium">Bottone Secondario (outline)</p>
                      <div className="space-y-2">
                        <label className="text-xs text-text-muted">Testo</label>
                        <Input
                          placeholder="es. Conosci i Produttori"
                          value={cmsForm.hero_cta_secondary_text}
                          onChange={(e) => setCmsForm(p => ({ ...p, hero_cta_secondary_text: e.target.value }))}
                        />
                      </div>
                      <div className="space-y-2">
                        <label className="text-xs text-text-muted">URL (path relativo)</label>
                        <Input
                          placeholder="es. /produttori"
                          value={cmsForm.hero_cta_secondary_url}
                          onChange={(e) => setCmsForm(p => ({ ...p, hero_cta_secondary_url: e.target.value }))}
                        />
                      </div>
                    </div>
                  </div>

                  <div className="flex justify-end pt-2 border-t border-border">
                    <Button onClick={handleCmsSave} disabled={cmsSaving} className="gap-2">
                      {cmsSaving ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle className="w-4 h-4" />}
                      {cmsSaving ? 'Salvataggio...' : 'Salva Homepage'}
                    </Button>
                  </div>
                </CardContent>
              </Card>
              {/* Pagine Principali: FOOD / ARTIGIANATO / EXPERIENCE */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <LayoutTemplate className="w-5 h-5 text-indigo-600" />
                    Pagine Principali
                  </CardTitle>
                  <CardDescription>
                    Personalizza titolo, descrizione e immagine banner delle 3 sezioni principali (Food, Artigianato, Experience)
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="space-y-6">
                    {['FOOD', 'ARTIGIANATO', 'EXPERIENCE'].map(mt => (
                      <div key={mt} className="border border-border rounded-lg p-4 space-y-4">
                        <h4 className="font-medium text-sm">{MACRO_LABELS[mt]}</h4>
                        <div className="flex items-start gap-4">
                          {/* Banner image */}
                          <div className="flex-shrink-0 space-y-1">
                            <div
                              className="relative w-32 h-20 rounded overflow-hidden border border-border bg-muted cursor-pointer hover:opacity-80 transition-opacity"
                              onClick={() => {
                                const input = document.createElement('input');
                                input.type = 'file'; input.accept = 'image/*';
                                input.onchange = (e) => handleMacroImageUpload(mt, e.target.files?.[0]);
                                input.click();
                              }}
                            >
                              {macroImgPreviews[mt] ? (
                                <img src={macroImgPreviews[mt]} alt={mt} className="w-full h-full object-cover" />
                              ) : (
                                <div className="w-full h-full flex items-center justify-center text-text-muted">
                                  <ImageIcon className="w-6 h-6" />
                                </div>
                              )}
                              {macroImgLoading[mt] && (
                                <div className="absolute inset-0 bg-black/50 flex items-center justify-center">
                                  <Loader2 className="w-4 h-4 text-white animate-spin" />
                                </div>
                              )}
                            </div>
                            <p className="text-xs font-medium text-primary text-center">1400 × 500 px</p>
                            <p className="text-xs text-text-muted text-center">ratio 3:1</p>
                          </div>
                          {/* Testo */}
                          <div className="flex-1 grid grid-cols-1 gap-3">
                            <div className="space-y-1">
                              <label className="text-xs font-medium">Titolo</label>
                              <Input
                                value={macroForms[mt]?.title || ''}
                                onChange={(e) => setMacroForms(p => ({ ...p, [mt]: { ...p[mt], title: e.target.value } }))}
                              />
                            </div>
                            <div className="space-y-1">
                              <label className="text-xs font-medium">Descrizione</label>
                              <Input
                                value={macroForms[mt]?.description || ''}
                                onChange={(e) => setMacroForms(p => ({ ...p, [mt]: { ...p[mt], description: e.target.value } }))}
                              />
                            </div>
                          </div>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => handleMacroSave(mt)}
                            disabled={macroSaving[mt]}
                            className="flex-shrink-0"
                          >
                            {macroSaving[mt] ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Salva'}
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>

              {/* Regioni */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <MapPin className="w-5 h-5 text-orange-600" />
                    Regioni
                  </CardTitle>
                  <CardDescription>
                    Modifica nome, descrizione e immagine di ogni regione
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    {regions.map(region => (
                      <div key={region.id} className="border border-border rounded-lg p-4 space-y-3">
                        <div className="flex items-start gap-4">
                          {/* Immagine */}
                          <div className="flex-shrink-0">
                            <div
                              className="relative w-24 h-16 rounded overflow-hidden border border-border bg-muted cursor-pointer hover:opacity-80 transition-opacity"
                              onClick={() => {
                                const input = document.createElement('input');
                                input.type = 'file';
                                input.accept = 'image/*';
                                input.onchange = (e) => handleRegionImageUpload(region.id, e.target.files?.[0]);
                                input.click();
                              }}
                            >
                              {region.image_url ? (
                                <img src={region.image_url} alt={region.name} className="w-full h-full object-cover" />
                              ) : (
                                <div className="w-full h-full flex items-center justify-center text-text-muted">
                                  <ImageIcon className="w-6 h-6" />
                                </div>
                              )}
                              {regionImgLoading[region.id] && (
                                <div className="absolute inset-0 bg-black/50 flex items-center justify-center">
                                  <Loader2 className="w-4 h-4 text-white animate-spin" />
                                </div>
                              )}
                            </div>
                            <p className="text-xs font-medium text-primary mt-1 text-center">1200 × 600 px</p>
                          </div>
                          {/* Campi */}
                          <div className="flex-1 grid grid-cols-1 md:grid-cols-2 gap-3">
                            <div className="space-y-1">
                              <label className="text-xs font-medium">Nome</label>
                              <Input
                                value={regionForms[region.id]?.name || ''}
                                onChange={(e) => setRegionForms(p => ({ ...p, [region.id]: { ...p[region.id], name: e.target.value } }))}
                              />
                            </div>
                            <div className="space-y-1">
                              <label className="text-xs font-medium">Descrizione</label>
                              <Input
                                value={regionForms[region.id]?.description || ''}
                                onChange={(e) => setRegionForms(p => ({ ...p, [region.id]: { ...p[region.id], description: e.target.value } }))}
                              />
                            </div>
                          </div>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => handleRegionSave(region.id)}
                            disabled={regionSaving[region.id]}
                            className="flex-shrink-0"
                          >
                            {regionSaving[region.id] ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Salva'}
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>

              {/* Sottocategorie */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <FolderTree className="w-5 h-5 text-purple-600" />
                    Sottocategorie
                  </CardTitle>
                  <CardDescription>
                    Modifica nome, descrizione e immagine delle singole sottocategorie (es. Ceramiche, Olio EVO…)
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    {allCategories.map(cat => (
                      <div key={cat.id} className="border border-border rounded-lg p-4 space-y-3">
                        <div className="flex items-start gap-4">
                          {/* Immagine */}
                          <div className="flex-shrink-0">
                            <div
                              className="relative w-24 h-16 rounded overflow-hidden border border-border bg-muted cursor-pointer hover:opacity-80 transition-opacity"
                              onClick={() => {
                                const input = document.createElement('input');
                                input.type = 'file';
                                input.accept = 'image/*';
                                input.onchange = (e) => handleCategoryImageUpload(cat.id, e.target.files?.[0]);
                                input.click();
                              }}
                            >
                              {cat.image_url ? (
                                <img src={cat.image_url} alt={cat.name} className="w-full h-full object-cover" />
                              ) : (
                                <div className="w-full h-full flex items-center justify-center text-text-muted">
                                  <ImageIcon className="w-6 h-6" />
                                </div>
                              )}
                              {categoryImgLoading[cat.id] && (
                                <div className="absolute inset-0 bg-black/50 flex items-center justify-center">
                                  <Loader2 className="w-4 h-4 text-white animate-spin" />
                                </div>
                              )}
                            </div>
                            <p className="text-xs font-medium text-primary text-center">800 × 400 px</p>
                            <Badge className={`mt-0.5 text-xs w-full justify-center ${getMacroTypeBadge(cat.macro_type)}`}>{cat.macro_type}</Badge>
                          </div>
                          {/* Campi */}
                          <div className="flex-1 grid grid-cols-1 md:grid-cols-2 gap-3">
                            <div className="space-y-1">
                              <label className="text-xs font-medium">Nome</label>
                              <Input
                                value={categoryForms[cat.id]?.name || ''}
                                onChange={(e) => setCategoryForms(p => ({ ...p, [cat.id]: { ...p[cat.id], name: e.target.value } }))}
                              />
                            </div>
                            <div className="space-y-1">
                              <label className="text-xs font-medium">Descrizione</label>
                              <Input
                                value={categoryForms[cat.id]?.description || ''}
                                onChange={(e) => setCategoryForms(p => ({ ...p, [cat.id]: { ...p[cat.id], description: e.target.value } }))}
                              />
                            </div>
                          </div>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => handleCategorySave(cat.id)}
                            disabled={categorySaving[cat.id]}
                            className="flex-shrink-0"
                          >
                            {categorySaving[cat.id] ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Salva'}
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>

            </div>
          </TabsContent>

          {/* SUPER_ADMIN ONLY: Categories Management Tab */}
          {isSuperAdmin && (
            <TabsContent value="categories">
              <div className="space-y-6">
                {/* Form Creazione Categoria */}
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <Plus className="w-5 h-5" />
                      Nuova Categoria
                    </CardTitle>
                    <CardDescription>
                      Crea una nuova categoria per il marketplace. Solo SuperAdmin può creare categorie.
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    <form onSubmit={handleCreateCategory} className="space-y-4">
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div className="space-y-2">
                          <label className="text-sm font-medium">Nome *</label>
                          <Input
                            placeholder="es. Ceramiche Artistiche"
                            value={newCategory.name}
                            onChange={(e) => {
                              const name = e.target.value;
                              setNewCategory(prev => ({
                                ...prev,
                                name,
                                slug: generateSlug(name)
                              }));
                            }}
                          />
                        </div>
                        <div className="space-y-2">
                          <label className="text-sm font-medium">Slug *</label>
                          <Input
                            placeholder="es. ceramiche-artistiche"
                            value={newCategory.slug}
                            onChange={(e) => setNewCategory(prev => ({ ...prev, slug: e.target.value }))}
                          />
                        </div>
                      </div>
                      
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div className="space-y-2">
                          <label className="text-sm font-medium">Macro Tipo *</label>
                          <Select 
                            value={newCategory.macro_type} 
                            onValueChange={(v) => setNewCategory(prev => ({ ...prev, macro_type: v }))}
                          >
                            <SelectTrigger>
                              <SelectValue placeholder="Seleziona tipo" />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="FOOD">FOOD - Prodotti Alimentari</SelectItem>
                              <SelectItem value="ARTIGIANATO">ARTIGIANATO - Prodotti Artigianali</SelectItem>
                              <SelectItem value="EXPERIENCE">EXPERIENCE - Esperienze</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                        <div className="space-y-2">
                          <label className="text-sm font-medium">Descrizione</label>
                          <Input
                            placeholder="Descrizione della categoria"
                            value={newCategory.description}
                            onChange={(e) => setNewCategory(prev => ({ ...prev, description: e.target.value }))}
                          />
                        </div>
                      </div>
                      
                      <Button 
                        type="submit" 
                        disabled={actionLoading.createCategory || !newCategory.name || !newCategory.slug}
                        className="gap-2"
                      >
                        {actionLoading.createCategory ? (
                          <Loader2 className="w-4 h-4 animate-spin" />
                        ) : (
                          <Plus className="w-4 h-4" />
                        )}
                        Crea Categoria
                      </Button>
                    </form>
                  </CardContent>
                </Card>

                {/* Lista Categorie */}
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <FolderTree className="w-5 h-5" />
                      Categorie Esistenti ({categories.length})
                    </CardTitle>
                    <CardDescription>
                      Elenco di tutte le categorie del marketplace raggruppate per macro tipo.
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="overflow-x-auto">
                      <table className="w-full text-sm">
                        <thead className="bg-muted/50">
                          <tr>
                            <th className="p-3 text-left font-medium">Nome</th>
                            <th className="p-3 text-left font-medium">Slug</th>
                            <th className="p-3 text-left font-medium">Macro Tipo</th>
                            <th className="p-3 text-left font-medium">Descrizione</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y">
                          {categories.map((cat) => (
                            <tr key={cat.id} className="hover:bg-muted/30">
                              <td className="p-3 font-medium">{cat.name}</td>
                              <td className="p-3 text-muted-foreground">{cat.slug}</td>
                              <td className="p-3">
                                <Badge className={getMacroTypeBadge(cat.macro_type)}>
                                  {cat.macro_type}
                                </Badge>
                              </td>
                              <td className="p-3 text-muted-foreground max-w-xs truncate">
                                {cat.description || '-'}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </CardContent>
                </Card>
              </div>
            </TabsContent>
          )}
        </Tabs>
      </div>
    </div>
  );
};

export default AdminDashboard;
